import { useEffect, useMemo, useState } from 'react';

import { ShoppingCartOutlined } from '@mui/icons-material';
import { Alert, Box, Divider } from '@mui/material';

import { BottomSheet } from 'components/docked';
import { CissTextField } from 'components/fields';
import { usePedidoCompraContext } from 'context/PedidoCompraContext';
import { PedidoCartInfo, PedidoCartProduct } from 'pages/compra/pedido/components';

interface PedidoCartBottomSheet {
    setOpen: (action: boolean) => void;
    open: boolean;
}

export function PedidoCartBottomSheet({ open, setOpen }: PedidoCartBottomSheet) {
    const { isEmptyCart, produtoCartList } = usePedidoCompraContext();
    const [search, setSearch] = useState<string>('');
    const [emptyAlertEl, setEmptyAlertEl] = useState<HTMLDivElement | null>(null);
    const [searchFieldEl, setSearchFieldEl] = useState<HTMLDivElement | null>(null);
    const [productsListEl, setProductsListEl] = useState<HTMLDivElement | null>(null);
    const [dividerEl, setDividerEl] = useState<HTMLDivElement | null>(null);
    const [infoEl, setInfoEl] = useState<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!open) {
            setSearch('');
        } else if (productsListEl?.parentElement) {
            productsListEl.parentElement.scrollTop = 0;
        }
    }, [open, productsListEl]);

    const produtoCartListFiltered = useMemo(() => {
        const filteredList = produtoCartList.filter((produtoCart) => {
            const { dsProduto } = produtoCart.produto;
            const includeSearch = dsProduto.toLowerCase().includes(search.toLowerCase());
            return includeSearch;
        });

        return filteredList.map((produtoCart) => <PedidoCartProduct key={produtoCart.produto.idProduto} produtoCart={produtoCart} />);
    }, [produtoCartList, search]);

    return (
        <BottomSheet
            open={open}
            minHeight={0}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            title={
                <>
                    <ShoppingCartOutlined />
                    Carrinho
                </>
            }
            measureElements={[emptyAlertEl, searchFieldEl, productsListEl, dividerEl, infoEl]}
        >
            {isEmptyCart && (
                <Alert ref={setEmptyAlertEl} sx={{ m: 0, flexShrink: 0 }} severity="info">
                    Sem produtos no pedido atual
                </Alert>
            )}

            {!isEmptyCart && (
                <>
                    <Box ref={setSearchFieldEl} sx={{ flexShrink: 0 }}>
                        <CissTextField
                            label="Pesquisar"
                            placeholder="Nome do produto"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            size="small"
                            fullWidth
                        />
                    </Box>

                    <Box sx={{ flex: '1 1 0', minHeight: 0, overflowY: 'auto' }}>
                        <Box ref={setProductsListEl} sx={{ 'display': 'flex', 'flexDirection': 'column', '> hr:last-of-type': { display: 'none' } }}>
                            {produtoCartListFiltered}

                            {!produtoCartListFiltered.length && <Alert severity="info">Sem produtos correspondentes</Alert>}
                        </Box>
                    </Box>
                </>
            )}

            <Divider ref={setDividerEl} sx={{ mx: -2, flexShrink: 0 }} />

            <Box ref={setInfoEl} sx={{ flexShrink: 0 }}>
                <PedidoCartInfo />
            </Box>
        </BottomSheet>
    );
}
